import React from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { darken, withStyles } from '@material-ui/core/styles';

import { statGreen, statGrey, statOrange, statRed } from '../../globals';
import { MAP_HEIGHT, MAP_WIDTH } from './WorldMapSvg';
import { autoFormatUnit } from '../../utils';


const styles = theme => ({
  marker: {
    fillOpacity: 0.8,
    cursor: 'pointer',
  },
  active: {
    strokeWidth: 2,
    '&$green': {
      stroke: darken(statGreen, 0.2),
    },
    '&$orange': {
      stroke: darken(statOrange, 0.2),
    },
    '&$red': {
      stroke: darken(statRed, 0.2),
    },
    '&$grey': {
      stroke: darken(statGrey, 0.2),
    },
  },
  green: {
    fill: statGreen,
  },
  orange: {
    fill: statOrange,
  },
  red: {
    fill: statRed,
  },
  grey: {
    fill: statGrey,
  },
  text: {
    height: '100%',
    color: 'white',
    userSelect: 'none',
    cursor: 'pointer',
  },
  subtitle: {
    fontSize: '65%',
  },
});


@withStyles(styles)
class MapMarker extends React.Component {
  static propTypes = {
    latitude: PropTypes.number.isRequired,
    longitude: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
    subtitle: PropTypes.string,
    active: PropTypes.bool,
    minValue: PropTypes.number,
    maxValue: PropTypes.number.isRequired,
    unit: PropTypes.string,
    variant: PropTypes.oneOf(['circle', 'rect']),
    className: PropTypes.string,
    onClick: PropTypes.func,
  };

  static defaultProps = {
    active: false,
    subtitle: null,
    minValue: 0,
    unit: null,
    variant: 'circle',
    className: null,
    onClick: null,
  };

  get absY() {
    return MAP_HEIGHT * (90 - this.props.latitude) / 180;
  }

  get absX() {
    return MAP_WIDTH * (this.props.longitude + 180) / 360;
  }

  get minRadius() {
    return (this.props.unit === '%' ? 0.08 : 0.05) * Math.min(MAP_WIDTH, MAP_HEIGHT);
  }

  get maxRadius() {
    return (this.props.unit === '%' ? 0.11 : 0.15) * Math.min(MAP_WIDTH, MAP_HEIGHT);
  }

  get valueRatio() {
    const { value, minValue, maxValue } = this.props;
    if (minValue === maxValue) {
      return 1.0;
    }
    return (Math.abs(value) - minValue) / (maxValue - minValue);
  }

  get fontSize() {
    const { unit } = this.props;
    const minFontSize = unit === '%' ? 9 : 7;
    const maxFontSize = unit === '%' ? 12 : 16;
    return minFontSize + this.valueRatio * (maxFontSize - minFontSize);
  }

  get radius() {
    return this.minRadius + this.valueRatio * (this.maxRadius - this.minRadius);
  }

  render() {
    const {
      value, subtitle, active, unit, variant, className, onClick, classes,
    } = this.props;
    const classNames = [classes.marker];
    if (className) {
      classNames.push(classes[className]);
    }
    if (active) {
      classNames.push(classes.active);
    }
    return (
      <>
        {
          variant === 'circle'
            ? (
              <circle
                cx={this.absX}
                cy={this.absY}
                r={this.radius}
                className={classNames.join(' ')}
                onClick={onClick}
              />
            )
            :
            (
              <rect
                x={this.absX - this.radius} y={this.absY - this.radius}
                width={this.radius * 2} height={this.radius * 2}
                className={classNames.join(' ')}
                onClick={onClick}
              />
            )
        }
        <foreignObject x={this.absX - this.radius} y={this.absY - this.radius} width={this.radius * 2} height={this.radius * 2}>
          <Grid
            container
            direction="column"
            justify="center"
            alignItems="center"
            onClick={onClick}
            className={classes.text}
            style={{ fontSize: this.fontSize }}
          >
            <Grid item>
              {autoFormatUnit(value, unit)}
            </Grid>
            <Grid item className={classes.subtitle}>
              {subtitle}
            </Grid>
          </Grid>
        </foreignObject>
      </>
    );
  }
}


export default MapMarker;
